import React from "react"
import { Box, Link, Stack, Typography } from "@mui/material"
import { AppCardTitle, AppNote, AppTableSkeleton } from "@components"
import { dsColorNew } from "@palette"
import { DS_REPRICING_ROUTES } from "@constants/routes"
import { dsRepricingDrawerData } from "Pages/DsRepricing/Page/@signals"
import { useRateCardFaces } from "Pages/DsRepricing/Page/@services/rateCardFaces"
import { rateCardsThatFit, fitScopeSummary } from "Pages/DsRepricing/Page/@services/rateCardsFit"
import { faceLabelFull } from "Pages/DsRepricing/Setup/@services/faceLabel"
import { useAxisCatalog } from "Pages/DsRepricing/Setup/@services/axes"
import { useSiteCatalog } from "Pages/DsRepricing/Setup/@services/sites"
import AxisChip from "Pages/DsRepricing/Setup/@components/AxisChip"

// "Rate cards that fit" (CU-869fh646w, UX decision D4): the consequence
// of the seven decisions of the scope step, at the end of it, in a box
// with no number because it is not a decision of the operator's. The
// fit depends on card 3 (tariff type, insurance) and card 5 (sites),
// which live in different groups, so the block lives under a group
// label of its own rather than inside either.
//
// Recomputed from the form signal on every change, saved by nobody.
// Never blocks Save (brief, decision 4: it warns): a strategy no rate
// card fits is a valid strategy that will decide nothing, and the red
// says exactly that and no more — fitting is necessary, not enough.
const rateCardsHref = `${DS_REPRICING_ROUTES.SETUP_PATH}?tab=rate-cards`

/** The fit of the form in the drawer, for this block and for the footer's red line. */
export function useRateCardsFit() {
	const { faces, loading, failed } = useRateCardFaces()
	const form = dsRepricingDrawerData.value || {}
	const fit = loading || failed ? [] : rateCardsThatFit(faces, form)
	// Unknown is not none: while loading or after a failed load nothing
	// is red (UX D4, the colour lie of the diary 2026-10-12).
	const noFit = !loading && !failed && fit.length === 0
	return { fit, faces, loading, failed, noFit, form }
}

function SetupLink() {
	return (
		<Link href={rateCardsHref} target="_blank" rel="noopener noreferrer">
			Setup → Rate cards
		</Link>
	)
}

function FitRow({ face }) {
	const sites = useSiteCatalog()
	const axes = useAxisCatalog()
	const blankTariff = face.is_prepaid == null
	const blankInsurance = face.ps_insurance_id == null
	const blankFranchise = face.ps_provider_id == null
	return (
		<Stack direction="row" alignItems="center" gap={1} flexWrap="wrap"
		       title={faceLabelFull(face, sites, axes)}
		       sx={{ py: 0.75, borderBottom: `1px solid ${dsColorNew.grayBorder}`,
		             '&:last-of-type': { borderBottom: 'none' } }}>
			<Typography sx={{ fontWeight: 600, fontSize: 13, color: dsColorNew.textDark, minWidth: 140 }}>
				{sites.labelOf(face.site_id)}
			</Typography>
			<AxisChip variant="tariff" blank={blankTariff}
			          label={blankTariff ? undefined : axes.prepaidName(Boolean(Number(face.is_prepaid)))}/>
			<AxisChip variant="insurance" blank={blankInsurance}
			          label={blankInsurance ? undefined : axes.insuranceName(face.ps_insurance_id)}/>
			<AxisChip variant="franchise" blank={blankFranchise}
			          label={blankFranchise ? undefined : axes.franchiseName(face.ps_provider_id)}/>
		</Stack>
	)
}

function Body({ fit, faces, loading, failed, form }) {
	const sites = useSiteCatalog()
	const axes = useAxisCatalog()
	if (loading) {
		return (
			<Box>
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, mb: 1 }}>Checking rate cards…</Typography>
				<AppTableSkeleton rows={2} columns={4}/>
			</Box>
		)
	}
	if (failed) {
		return (
			<AppNote severity="warning">
				Could not load the rate cards, so the fit cannot be shown. Saving is still
				allowed; check Setup → Rate cards after saving.
			</AppNote>
		)
	}
	if (faces.length === 0) {
		return (
			<AppNote severity="error">
				<strong>No rate cards yet — this strategy will decide nothing.</strong>{' '}
				Nothing can be priced until a rate card exists. Create one in <SetupLink/>;
				reload this page to see it here.
			</AppNote>
		)
	}
	if (fit.length === 0) {
		return (
			<AppNote severity="error">
				<strong>No rate card fits this scope — this strategy will decide nothing.</strong>{' '}
				{fitScopeSummary(form, { labelOf: sites.labelOf, prepaidName: axes.prepaidName,
				                         insuranceName: axes.insuranceName })}.
				Widen the sites or the filters above, or add a rate card in <SetupLink/>.
				You can still save it.
			</AppNote>
		)
	}
	return (
		<Box>
			{fit.map(face => <FitRow key={face.id} face={face}/>)}
		</Box>
	)
}

export default function DsRepricingRateCardsFit() {
	const state = useRateCardsFit()
	return (
		// The look of `SectionCard`, without the number: it is not a decision.
		<Box component="section" sx={{ bgcolor: dsColorNew.white, borderRadius: '14px', p: 2.25,
		                               border: `1px solid ${dsColorNew.grayBorder}`, minWidth: 0 }}>
			<AppCardTitle icon="layer-group" title="Rate cards that fit" sx={{ mb: 1.5 }}
			              subtitle={<>
				              Rate cards whose site is inside the sites above and whose tariff type and
				              insurance meet the filters of card 3. A blank axis on the rate card fits any
				              filter. Fitting is necessary, not enough: office, pickup dates and rental
				              length still decide on each pass.
			              </>}/>
			<Body {...state}/>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, display: 'block', mt: 1.5 }}>
				Recomputed as you change sites, tariff type or insurance — nothing is saved until you do.
			</Typography>
		</Box>
	)
}
